import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Skeleton,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import { Line } from "react-chartjs-2";
import shiColors from "shared-ui/src/theme/shiColors";
import {
  Chart,
  Legend,
  LineElement,
  PointElement,
  Tooltip as ChartJSTooltip,
} from "chart.js";
import useAdjustCategoriesChartPositions from "@features/assessments-feature/hooks/useAdjustCategoriesChartPositions";
import DemoModeOverlay from "@features/assessments-feature/components/DemoModeOverlay";
import useAssessmentDemoData from "@features/assessments-feature/hooks/useAssessmentDemoData";
import { useChartJsDefaults } from "shared-ui/src/theme/useChartJsDefaults";
import {
  convertChartPositionToLabel,
  convertScoreToChartPosition,
  getOrderedMaturityLevels,
  shouldHideTargetMaturity,
  shouldUseTextLabels,
} from "@features/assessments-feature/utils/maturityScoreUtils";
import useGetAssessmentOverview from "@features/assessments-feature/hooks/useGetAssessmentOverview";
import assessmentViewsMap from "@features/assessments-feature/utils/assessmentViewsMap";
import { useAssessmentViewContext } from "@features/assessments-feature/contexts-providers/AssessmentViewContextProvider";

Chart.register(LineElement, PointElement, Legend, ChartJSTooltip);

const AssessmentOverviewTargetMaturityBreakdown = () => {
  useChartJsDefaults();
  const { assessmentId, program } = useAssessmentViewContext();
  const assessmentProgram = assessmentViewsMap.get(program ?? "")?.id;
  const { isDemoMode } = useAssessmentDemoData();
  const { data, isLoading } = useGetAssessmentOverview(
    assessmentProgram,
    assessmentId ?? undefined,
  );
  const { customLabelsPlugin, labelHeight, labelPosition, labelWidth } =
    useAdjustCategoriesChartPositions();

  const useTextLabels = shouldUseTextLabels(assessmentProgram);
  const maturityLevels = getOrderedMaturityLevels();

  const subCategoriesList: string[] = [];
  const targetMaturityList: number[] = [];
  const currentMaturityList: number[] = [];

  data?.targetMaturityBreakdown?.forEach((d) => {
    subCategoriesList.push(d.subCategory ?? "");
    let targetValue = d.targetMaturity
      ? Number(d.targetMaturity.toFixed(1))
      : 0;
    let currentValue = d.currentMaturity
      ? Number(d.currentMaturity.toFixed(1))
      : 0;

    if (useTextLabels) {
      targetValue = convertScoreToChartPosition(targetValue);
      currentValue = convertScoreToChartPosition(currentValue);
    }

    targetMaturityList.push(targetValue);
    currentMaturityList.push(currentValue);
  });

  const options = {
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    layout: {
      padding: {
        bottom: 64,
        right: 8,
      },
    },
    scales: {
      x: {
        ticks: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        max: useTextLabels ? maturityLevels.length - 1 : 5,
        ticks: {
          stepSize: 1,
          callback: function (value: any) {
            if (useTextLabels) {
              const index = Math.floor(value);
              if (index >= 0 && index < maturityLevels.length) {
                return maturityLevels[index];
              }
              return "";
            }
            return value;
          },
        },
      },
    },
    plugins: {
      legend: {
        position: "top" as const,
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (useTextLabels) {
              // Convert chart position back to text label
              const chartPosition = context.parsed.y;
              const textLabel = convertChartPositionToLabel(chartPosition);
              label += textLabel;
            } else {
              label += context.parsed.y;
            }
            return label;
          },
        },
      },
      customLabelsPlugin: {},
    },
  };

  const hideTarget = shouldHideTargetMaturity(assessmentProgram);

  const chartData = {
    labels: subCategoriesList,
    datasets: [
      {
        label: "Current Maturity",
        data: currentMaturityList,
        borderColor: shiColors.blue.DEFAULT,
        backgroundColor: shiColors.blue.DEFAULT,
        clip: false, // Allow rendering outside chart bounds
      },
      ...(!hideTarget
        ? [
            {
              label: "Target Maturity",
              data: targetMaturityList,
              borderColor: shiColors.pink.DEFAULT,
              backgroundColor: shiColors.pink.DEFAULT,
              clip: false, // Allow rendering outside chart bounds
            },
          ]
        : []),
    ],
  };

  return (
    <Card sx={{ position: "relative" }}>
      {assessmentProgram && isDemoMode.get(assessmentProgram) && (
        <DemoModeOverlay />
      )}
      <CardHeader
        title={hideTarget ? "Maturity breakdown" : "Target maturity breakdown"}
        slotProps={{
          title: { variant: "body1", textTransform: "uppercase" }
        }}
      />
      <Divider />
      <CardContent
        sx={{
          paddingLeft: 3,
          paddingRight: 4,
          paddingY: 2.5,
          height: "50vh",
          maxHeight: 450,
        }}
      >
        {isLoading ? (
          <Skeleton variant="rectangular" height={"47vh"} />
        ) : (
          <Box height={"100%"} sx={{ position: "relative" }}>
            <Line
              data={chartData}
              options={options}
              plugins={[customLabelsPlugin]}
            />
            {subCategoriesList.map((label, index) => {
              const key = label + index;
              return (
                <Box
                  key={key}
                  top={labelHeight}
                  left={labelPosition[index]}
                  position="absolute"
                  color={"text.secondary"}
                  display="flex"
                  alignItems="start"
                  justifyContent="center"
                  textAlign="center"
                  width={labelWidth[index]}
                  sx={{ transform: "translateX(-50%)" }}
                >
                  {labelPosition.length > 0 && (
                    <Typography variant={"caption"}>{label}</Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AssessmentOverviewTargetMaturityBreakdown;
